import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
  PROFILES,
  PROFILE_TABS,
  articleUnits,
  catalogSheets,
  guideTable,
  templateSheets,
  planWorkbook,
  importRecord,
  placeOptionKey,
  placeOptions,
  plannedBottles,
  rememberedChoices,
  stockLines,
  uuidv7,
  plannedReceipt,
} from "@lagerlog/core";
import type { Booking, Cell, ExistingArticle, ImportChoices, ImportNote, ImportPlan, ImportRecord, ImportSheet, State as LedgerState, PackUnit, Place, PlannedRow } from "@lagerlog/core";
import type { Catalog } from "../catalog.ts";
import { drinkProfile, keepsVintages } from "../catalog.ts";
import { menuOf } from "./Card.tsx";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { useWide } from "../wide.ts";

/** A small CSV reader: semicolon or comma, quotes as Excel writes them. */
export function readCsv(text: string): Cell[][] {
  const first = text.split(/\r?\n/, 1)[0] ?? "";
  const sep = (first.match(/;/g)?.length ?? 0) >= (first.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: Cell[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') [cell, i] = [cell + '"', i + 1];
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) [row, cell] = [[...row, cell], ""];
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      rows.push([...row, cell]);
      [row, cell] = [[], ""];
    } else cell += ch;
  }
  if (cell !== "" || row.length) rows.push([...row, cell]);
  return rows;
}

const WIDE_COLUMNS = ["Erzeuger", "Hersteller", "Hersteller oder Marke", "Brauerei", "Name", "Lagerort", "Appellation oder Lage", "Rebsorten", "Kartentext"];

/** The second sheet: what each column means, how to write it, what it does (IMPORT_GUIDE). */
const guide = () =>
  guideTable().map((r, i) => r.map((c) => (c === "" ? null : { value: c, wrap: true, ...(i === 0 || (r[1] === "" && r[0]) ? { fontWeight: "bold" as const } : {}) })));
const GUIDE_WIDTHS = [22, 8, 40, 26, 70].map((width) => ({ width }));

/** An Excel file with a sheet per profile (the wines first) and the guide after them. */
async function writeBook(sheets: ImportSheet[], fileName: string) {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const cell = (c: Cell, head: boolean) => (c === null || c === undefined ? null : head ? { value: String(c), fontWeight: "bold" as const } : { value: c });
  await writeXlsxFile(
    [
      ...sheets.map((sh) => ({
        data: sh.rows.map((r, i) => r.map((c) => cell(c, i === 0))),
        sheet: sh.name,
        columns: (sh.rows[0] ?? []).map((h) => ({ width: WIDE_COLUMNS.includes(String(h)) ? 24 : 14 })),
        stickyRowsCount: 1,
      })),
      { data: guide(), sheet: "Erklärungen", columns: GUIDE_WIDTHS, stickyRowsCount: 1 },
    ] as never,
  ).toFile(fileName);
}

/** „Zeile 4“, in a workbook with its sheet: „Biere, Zeile 4“. */
const where = (n: { line: number; sheet?: string }) => `${n.sheet ? `${n.sheet}, ` : ""}Zeile ${n.line}`;
const lineKey = (n: { line: number; sheet?: string }) => `${n.sheet ?? ""}|${n.line}`;

const NO_CHOICES: ImportChoices = { colors: {}, places: {} };
/** The container of a planned row („Fl.“, „Fass“): the row's fields over those of the known article. */
const unitOfRow = (catalog: Catalog, r: PlannedRow): PackUnit => articleUnits({ ...catalog.articles.get(r.id)?.fields, ...r.fields }).units[0]!;
const inUnit = (n: number, u: PackUnit) => `${n} ${n === 1 ? u.name : (u.plural ?? u.name)}`;
const lines = (n: number) => `${n} ${n === 1 ? "Zeile" : "Zeilen"}`;
const when = (at: number) => new Date(at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function existingOf(catalog: Catalog, ledger: LedgerState): ExistingArticle[] {
  const stock = new Map<string, number>();
  for (const l of stockLines(ledger)) stock.set(l.article, (stock.get(l.article) ?? 0) + l.qty);
  // In bottles, as the table counts them.
  return [...catalog.articles.values()].map((a) => ({ id: a.id, fields: a.fields, stock: (stock.get(a.id) ?? 0) / a.bottle }));
}

/** The places of the storages this device holds: only there the import books opening stock. */
function placesOf(catalog: Catalog, held: string[]): Place[] {
  return [...catalog.locations.values()]
    .filter((l) => held.includes(l.lager))
    .map((l) => {
      const lagerName = catalog.lagers.get(l.lager)?.name ?? l.lager;
      return { id: l.id, lager: l.lager, label: `${lagerName} · ${l.name}`, lagerName, name: l.name };
    });
}

/**
 * The catalogue as a table (P1-4): the template, the catalogue to take away, and an upload with a
 * preview before anything is written (rules in packages/core/src/catalog-import.ts). Colours and
 * places the import does not know are asked once each; what was taken, left out and decided is
 * kept as a record of type `import` and shown under „Letzte Importe“.
 */
export function Import({ onDone }: { onDone: () => void }) {
  const { store } = useStore();
  const catalog = useCatalog();
  const cellarDrinks = useDrinks();
  const ledger = useLedger();
  const [sheet, setSheet] = useState<{ sheets: ImportSheet[]; file: string } | null>(null);
  const [choices, setChoices] = useState<ImportChoices>(NO_CHOICES);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [done, setDone] = useState<{ text: string; record: string } | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const wide = useWide();
  const held = store.config?.lagers ?? [];

  // Computed for the table and the decisions only, not again while the sync changes the catalogue underneath.
  const plan = useMemo<ImportPlan | null>(() => {
    if (!sheet || !catalog || !ledger) return null;
    return planWorkbook(sheet.sheets, existingOf(catalog, ledger), placesOf(catalog, held), () => uuidv7(Date.now()), {
      lagers: [...catalog.lagers.values()].filter((l) => held.includes(l.id)),
      taken: [...catalog.lagers.keys(), ...catalog.locations.keys()],
      choices,
      remembered: rememberedChoices(catalog.imports.values()),
      rubrics: menuOf(catalog).rubrics,
      recipes: [...catalog.recipes.values()],
      vintages: keepsVintages(catalog),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, choices]);

  if (!catalog || !ledger) return null;
  // „Getränk“ once the cellar holds other drinks or the table brings them; only wines: „Wein“.
  const drinks = cellarDrinks || (plan?.rows ?? []).some((r) => (r.fields.profile ?? (catalog.articles.get(r.id) ? drinkProfile(catalog.articles.get(r.id)!) : "wein")) !== "wein");
  const N = drinks ? "Getränke" : "Weine";
  const ONE = drinks ? "Getränk" : "Wein";
  const places = placesOf(catalog, held);
  const lagers = [...catalog.lagers.values()].filter((l) => held.includes(l.id));
  const records = [...catalog.imports.values()].sort((a, b) => b.at - a.at).slice(0, 10);
  const record = shown ? catalog.imports.get(shown) : undefined;

  async function read(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setDone(null);
    setShown(null);
    try {
      // Every sheet: one per profile (Weine, Spirituosen, Biere …); the guide is passed over.
      let sheets: ImportSheet[];
      if (/\.(csv|txt)$/i.test(file.name)) sheets = [{ name: "", rows: readCsv(await file.text()) }];
      else {
        const { default: readXlsxFile } = await import("read-excel-file/browser");
        sheets = (await readXlsxFile(file)).map((s) => ({ name: s.sheet, rows: s.data as Cell[][] }));
      }
      setChoices(NO_CHOICES);
      setSheet({ sheets, file: file.name });
    } catch {
      setError("Die Datei ließ sich nicht lesen. Bitte als Excel-Datei (.xlsx) oder CSV speichern.");
    }
  }

  async function apply() {
    if (!plan || !sheet) return;
    const rows = plan.rows.filter((r) => r.action !== "same" || r.stock.length);
    const recipes = plan.recipes.filter((r) => r.action !== "same");
    const steps = plan.newLagers.length + plan.newPlaces.length + rows.length + recipes.length;
    let n = 0;
    const step = () => setProgress(`${++n} von ${steps} …`);
    for (const l of plan.newLagers) {
      step();
      await store.edit("lager", l.id, { name: l.name, profile: "wein", archived: false });
    }
    if (plan.newLagers.length) await store.setLagers([...held, ...plan.newLagers.map((l) => l.id)]);
    for (const p of plan.newPlaces) {
      step();
      await store.edit("location", p.id, { lager: p.lager, name: p.name, archived: false });
    }
    // The record's id goes with the opening stock: „Zuletzt gebucht“ shows the import as one line (B6).
    const id = uuidv7(Date.now());
    for (const r of rows) {
      step();
      if (Object.keys(r.fields).length) await store.edit("article", r.id, r.fields);
      for (const s of r.stock) {
        await store.book(plannedReceipt(r, s, id) as Booking);
      }
    }
    for (const r of recipes) {
      step();
      await store.edit("recipe", r.id, r.fields);
    }
    const person = store.config?.userId;
    await store.edit("import", id, importRecord(plan, { file: sheet.file, at: Date.now(), ...(person ? { person } : {}) }));
    setProgress(null);
    setSheet(null);
    setChoices(NO_CHOICES);
    const left = plan.errors.length ? `, ${lines(plan.errors.length)} ausgelassen` : "";
    const mixed = recipes.length ? ` und ${recipes.length} zubereitete` : "";
    const counted = rows.length === 0 ? `Keine ${N}` : `${rows.length} ${rows.length === 1 ? ONE : N}`;
    setDone({ text: `${counted}${mixed} übernommen${left}. Der Abgleich läuft im Hintergrund.`, record: id });
    if (wide) setShown(id);
  }

  // On a phone the record of an import is a page of its own.
  if (record && !wide) {
    return (
      <div className="import">
        <button className="link" onClick={() => setShown(null)}>
          ← {N} aus einer Tabelle
        </button>
        <ImportLog record={record} />
      </div>
    );
  }

  const count = (action: string) => plan?.rows.filter((r) => r.action === action).length ?? 0;
  const bottles = plan?.rows.reduce((s, r) => s + plannedBottles(r), 0) ?? 0;
  // Opening stock per container: kegs and cans apart from bottles.
  const byUnit = new Map<string, { n: number; unit: PackUnit }>();
  for (const r of plan?.rows ?? []) {
    if (!r.stock.length) continue;
    const unit = unitOfRow(catalog, r);
    const u = byUnit.get(unit.name) ?? { n: 0, unit };
    u.n += plannedBottles(r);
    byUnit.set(unit.name, u);
  }
  const onlyBottles = [...byUnit.keys()].every((k) => k === "Fl.");
  const placeLabel = (id: string) => places.find((p) => p.id === id)?.label ?? plan?.newPlaces.find((p) => p.id === id)?.label ?? id;
  const notesAt = new Map<string, string[]>();
  for (const n of plan?.notes ?? []) notesAt.set(lineKey(n), [...(notesAt.get(lineKey(n)) ?? []), n.message]);
  const nameAt = new Map<string, string>();
  for (const r of plan?.rows ?? []) for (const l of r.lines) nameAt.set(lineKey({ line: l, sheet: r.sheet }), r.name);

  return (
    <div className="import">
      <button className="link" onClick={onDone}>
        ← Alle {N}
      </button>
      <h2>{N} aus einer Tabelle</h2>
      <p className="muted">
        Für viele {N} auf einmal: die Vorlage in Excel ausfüllen und hier hochladen. Die Vorlage hat ein Blatt je Profil (Weine, Spirituosen, Biere, Alkoholfreie Getränke, Weitere Getränke). Eine eigene Tabelle geht auch, wenn sie Spalten wie „Erzeuger“, „Name“ und „Farbe“ hat. Dieselbe Tabelle lässt sich später wieder hochladen: Bekannte {N} werden
        geändert, nicht doppelt angelegt.
      </p>
      <div className="buttons">
        <button onClick={() => writeBook(templateSheets(), "KellerLog-Vorlage.xlsx")} data-testid="template">
          Vorlage herunterladen
        </button>
        <button onClick={() => writeBook(catalogSheets(existingOf(catalog, ledger), [...catalog.recipes.values()], menuOf(catalog).rubrics), "KellerLog-Weine.xlsx")}>Alles als Excel herunterladen</button>
      </div>
      <label className="upload">
        <span className="button primary">Tabelle hochladen</span>
        <input type="file" accept=".xlsx,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={read} hidden data-testid="upload" />
      </label>
      <p className="hint">Excel (.xlsx) oder CSV. Die Spalte „Bestand“ gilt nur beim ersten Erfassen eines {drinks ? "Getränkes" : "Weins"}; danach ändern ihn nur Buchungen.</p>
      {error && <p className="error-box">{error}</p>}
      {done && (
        <p className="ok-text" data-testid="import-done">
          {done.text}{" "}
          {!wide && (
            <button className="link" onClick={() => setShown(done.record)}>
              Logbuch ansehen
            </button>
          )}
        </p>
      )}

      {plan && sheet && (
        <section className="card" data-testid="import-plan">
          <h3>{sheet.file}</h3>
          <Decisions plan={plan} drinks={drinks} lagers={lagers} places={places} choices={choices} onChange={setChoices} />
          <ul className="summary">
            <li>
              <strong>{count("create")}</strong> {count("create") === 1 ? (drinks ? "neues Getränk" : "neuer Wein") : drinks ? "neue Getränke" : "neue Weine"}
            </li>
            <li>
              <strong>{count("update")}</strong> geändert
            </li>
            <li>
              <strong>{count("same")}</strong> unverändert
            </li>
            <li>
              {onlyBottles ? (
                <>
                  <strong>{bottles}</strong> Flaschen Startbestand
                </>
              ) : (
                <>
                  <strong>{[...byUnit.values()].map((u) => inUnit(u.n, u.unit)).join(" + ")}</strong> Startbestand
                </>
              )}
            </li>
            {plan.recipes.some((r) => r.action !== "same") && (
              <li>
                <strong>{plan.recipes.filter((r) => r.action !== "same").length}</strong> zubereitete Getränke neu oder geändert
              </li>
            )}
            {plan.newPlaces.length > 0 && (
              <li>
                <strong>{plan.newPlaces.length}</strong> {plan.newPlaces.length === 1 ? "neuer Lagerort" : "neue Lagerorte"}
              </li>
            )}
            {plan.errors.length > 0 && (
              <li className="negative">
                <strong>{plan.errors.length}</strong> ausgelassen
              </li>
            )}
          </ul>
          {wide ? (
            <PlanTable plan={plan} drinks={drinks} notesAt={notesAt} placeLabel={placeLabel} unitOf={(r) => unitOfRow(catalog, r)} />
          ) : (
            <>
              {plan.errors.length > 0 && (
                <>
                  <h3 className="error">
                    {lines(plan.errors.length)} {plan.errors.length === 1 ? "wird" : "werden"} nicht übernommen
                  </h3>
                  <ul className="list problems">
                    {plan.errors.map((e) => (
                      <li key={lineKey(e)}>
                        {where(e)}: {e.message}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {plan.notes.length > 0 && (
                <>
                  <h3>Hinweise</h3>
                  <ul className="list problems">
                    {[...notesAt].map(([key, messages]) => {
                      const [sheet, line] = key.split("|");
                      return (
                        <li key={key}>
                          {where({ line: Number(line), ...(sheet ? { sheet } : {}) })}, {nameAt.get(key)}: {messages.join("; ")}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {plan.rows.some((r) => r.action !== "same") && (
                <>
                  <h3>Vorschau</h3>
                  <ul className="list">
                    {plan.rows
                      .filter((r) => r.action !== "same")
                      .slice(0, 30)
                      .map((r) => (
                        <li key={lineKey(r)} className="row between">
                          <span>{r.name}</span>
                          <span className="side boxed muted">
                            <span className="stack">
                              <span>{r.action === "create" ? "neu" : "geändert"}</span>
                              {r.stock.length > 0 && <span>{inUnit(plannedBottles(r), unitOfRow(catalog, r))}</span>}
                            </span>
                          </span>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </>
          )}
          {plan.ignored.length > 0 && <p className="hint">Nicht verwendete Spalten: {plan.ignored.join(", ")}</p>}
          <div className="buttons">
            <button className="primary" disabled={progress !== null || !(plan.rows.some((r) => r.action !== "same" || r.stock.length) || plan.recipes.some((r) => r.action !== "same"))} onClick={apply} data-testid="import-apply">
              {progress ?? "Übernehmen"}
            </button>
            <button className="link" onClick={() => setSheet(null)}>
              Verwerfen
            </button>
          </div>
        </section>
      )}

      {!plan && records.length > 0 && (
        <div className={wide ? "split" : ""}>
          <section>
            <h3>Letzte Importe</h3>
            <ul className="list" data-testid="import-history">
              {records.map((r) => (
                <li key={r.id} className={shown === r.id ? "selected" : ""}>
                  <button className="row between" onClick={() => setShown(r.id)}>
                    <span>
                      {r.file}
                      <br />
                      <span className="muted">
                        {when(r.at)}
                        {store.personName(r.person) ? ` · ${store.personName(r.person)}` : ""}
                      </span>
                    </span>
                    <span className="side">
                      <span>{r.counts.created} neu</span>
                      <span className={r.counts.left ? "negative" : "muted"}>{r.counts.left} ausgelassen</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          {wide && <div className="side-pane">{record ? <ImportLog record={record} /> : <p className="muted placeholder">Ein Import links zeigt, was übernommen und was ausgelassen wurde.</p>}</div>}
        </div>
      )}
    </div>
  );
}

/** Values the import does not know: one decision each, for all their rows; remembered for the next upload. */
function Decisions({ plan, drinks, lagers, places, choices, onChange }: { plan: ImportPlan; drinks: boolean; lagers: { id: string; name: string }[]; places: Place[]; choices: ImportChoices; onChange: (c: ImportChoices) => void }) {
  if (!plan.colors.length && !plan.places.length) return null;
  const from = (f: string) => (f === "remembered" ? "wie beim letzten Mal" : f === "suggested" ? "Vorschlag" : "");
  return (
    <div className="banner-box decisions" data-testid="import-decisions">
      <strong>Bitte prüfen</strong>
      <p className="hint">Diese Angaben kennt die App noch nicht. Die Wahl gilt für alle ihre Zeilen und wird für das nächste Hochladen gemerkt.</p>
      {plan.colors.map((c) => (
        <div key={c.key} className="decision">
          <label>
            {c.empty ? (c.empty.guess ? `Farbe leer, der Name deutet auf ${c.empty.guess}` : "Farbe leer, kein Hinweis im Namen") : `Farbe „${c.value}“`} · {lines(c.lines.length)}
            <select value={c.color ?? ""} onChange={(e) => onChange({ ...choices, colors: { ...choices.colors, [c.key]: e.target.value || null } })} data-testid={`color-${c.key}`}>
              {PROFILES.map((p) => (
                <optgroup key={p.id} label={PROFILE_TABS[p.id]}>
                  {(p.fields.find((f) => f.key === p.categoryField)?.options ?? []).map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value="">Zeilen auslassen</option>
            </select>
          </label>
          {c.color && from(c.from) && <span className="hint">{from(c.from)}</span>}
          {c.empty && !c.empty.guess && c.empty.names.length > 1 && <span className="hint">Verschiedene Getränke? Dann besser die Farbe in der Tabelle eintragen.</span>}
          {c.empty && (
            <details>
              <summary>{c.empty.names.length === 1 ? (drinks ? "Welches Getränk?" : "Welcher Wein?") : `Welche ${c.empty.names.length} ${drinks ? "Getränke" : "Weine"}?`}</summary>
              {c.empty.names.join(", ")}
            </details>
          )}
        </div>
      ))}
      {plan.places.map((p) => {
        const options = placeOptions(p, lagers, places);
        const hint =
          p.from === "remembered"
            ? from(p.from)
            : p.target?.created
              ? "wird angelegt"
              : p.choice.kind === "place" && p.from === "suggested"
                ? "Vorschlag: ähnlich geschrieben"
                : !p.target && p.choice.kind !== "skip"
                  ? "kein Bestand zu buchen"
                  : "";
        return (
          <div key={p.key} className="decision">
            <label>
              Lagerort „{p.value}“ · {lines(p.lines.length)}, {p.bottles} {drinks ? "Gebinde" : "Fl."}
              <select value={placeOptionKey(p.choice)} onChange={(e) => onChange({ ...choices, places: { ...choices.places, [p.key]: options.find((o) => o.key === e.target.value)!.choice } })} data-testid={`place-${p.key}`}>
                <optgroup label="Neu anlegen">
                  {options
                    .filter((o) => o.group === "new")
                    .map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label.replace(/^Neu: /, "")}
                      </option>
                    ))}
                </optgroup>
                {places.length > 0 && (
                  <optgroup label="Vorhandener Lagerort">
                    {options
                      .filter((o) => o.group === "known")
                      .map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                  </optgroup>
                )}
                <option value="skip">Bestand nicht übernehmen</option>
              </select>
            </label>
            {hint && <span className="hint">{hint}</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Wide screens: every row of the table that does something, in the order of the table. */
function PlanTable({ plan, drinks, notesAt, placeLabel, unitOf }: { plan: ImportPlan; drinks: boolean; notesAt: Map<string, string[]>; placeLabel: (id: string) => string; unitOf: (r: PlannedRow) => PackUnit }) {
  type Entry = ({ name: string; what: string; qty: number; unit: PackUnit; place: string; note: string } | { error: string }) & ImportNote;
  const entries: Entry[] = plan.errors.map((e) => ({ ...e, error: e.message }));
  for (const r of plan.rows) {
    for (const line of r.lines) {
      const stock = r.stock.filter((s) => s.line === line);
      const note = (notesAt.get(lineKey({ line, sheet: r.sheet })) ?? []).join("; ");
      const first = line === r.line;
      if (!stock.length && !note && (!first || r.action === "same")) continue;
      const what = !first ? "zusammengefasst" : r.action === "create" ? "neu" : r.action === "update" ? "geändert" : "unverändert";
      entries.push({ line, message: "", ...(r.sheet ? { sheet: r.sheet } : {}), name: r.name, what, qty: stock.reduce((s, x) => s + x.qty, 0), unit: unitOf(r), place: stock.map((s) => placeLabel(s.location)).join(", "), note });
    }
  }
  entries.sort((a, b) => (a.sheet ?? "").localeCompare(b.sheet ?? "") || a.line - b.line);
  return (
    <table className="grid import-table">
      <thead>
        <tr>
          <th className="num">Zeile</th>
          <th>{drinks ? "Getränk" : "Wein"}</th>
          <th>Was passiert</th>
          <th className="num">Startbestand</th>
          <th>Lagerort</th>
          <th>Hinweis</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) =>
          "error" in e ? (
            <tr key={`e${lineKey(e)}`} className="negative">
              <td className="num">{e.sheet ? where(e) : e.line}</td>
              <td colSpan={5}>{e.error} · wird nicht übernommen</td>
            </tr>
          ) : (
            <tr key={`r${lineKey(e)}`}>
              <td className="num muted">{e.sheet ? where(e) : e.line}</td>
              <td>{e.name}</td>
              <td>{e.what}</td>
              <td className="num">{e.qty ? inUnit(e.qty, e.unit) : ""}</td>
              <td className="nowrap">{e.place}</td>
              <td className="muted">{e.note}</td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/** What an import took, left out and decided; kept for later (packages/core/src/import-record.ts). */
function ImportLog({ record }: { record: ImportRecord }) {
  const drinks = useDrinks();
  const { store } = useStore();
  const person = store.personName(record.person);
  const c = record.counts;
  return (
    <section className="card" data-testid="import-log">
      <h3>{record.file}</h3>
      <p className="muted">
        {when(record.at)}
        {person ? ` · ${person}` : ""}
      </p>
      <ul className="summary">
        <li>
          <strong>{c.created}</strong> {c.created === 1 ? (drinks ? "neues Getränk" : "neuer Wein") : drinks ? "neue Getränke" : "neue Weine"}
        </li>
        <li>
          <strong>{c.updated}</strong> geändert
        </li>
        <li>
          <strong>{c.bottles}</strong> {drinks ? "Gebinde" : "Flaschen"} Startbestand
        </li>
        <li className={c.left ? "negative" : ""}>
          <strong>{c.left}</strong> ausgelassen
        </li>
      </ul>
      {record.errors.length > 0 && (
        <>
          <h3 className="error">Nicht übernommen</h3>
          <ul className="list problems">
            {record.errors.map((e, i) => (
              <li key={i}>
                {where(e)}: {e.message}
              </li>
            ))}
          </ul>
          {record.more?.errors ? <p className="hint">… und {record.more.errors} weitere</p> : null}
        </>
      )}
      {record.notes.length > 0 && (
        <>
          <h3>Hinweise</h3>
          <ul className="list problems">
            {record.notes.map((n, i) => (
              <li key={i}>
                {where(n)}: {n.message}
              </li>
            ))}
          </ul>
          {record.more?.notes ? <p className="hint">… und {record.more.notes} weitere</p> : null}
        </>
      )}
      {record.decisions.length > 0 && (
        <>
          <h3>Zuordnungen</h3>
          <ul className="list problems">
            {record.decisions.map((d) => (
              <li key={`${d.column}|${d.key}`}>
                {d.key.startsWith("leer:") ? `Farbe leer (${lines(d.count ?? 0)})` : `${d.column === "color" ? "Farbe" : "Lagerort"} „${d.value}“`} → {d.label}
                {d.created ? " (neu angelegt)" : ""}
              </li>
            ))}
          </ul>
        </>
      )}
      {!record.errors.length && !record.notes.length && <p className="ok-text">Alle Zeilen übernommen.</p>}
    </section>
  );
}
